import React, { useState, useRef, useMemo, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import SplitConformanceGraph from "./SplitConformanceGraph";
import ConformanceSummary from "../../ConformanceChecker/components/ConformanceSummary";
import TemporalResults from "../../TemporalConformanceChecker/components/TemporalResults";
import EdgeDetailPanel from "./EdgeDetailPanel";

// ── Tooltip (identical to pattern used in ConformanceSummary) ─────────────────

const Tooltip = ({ text }) => {
  const [pos, setPos] = useState(null);
  const ref = useRef(null);
  const show = () => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    const above = r.top > 140;
    setPos({ left: r.left + r.width / 2, top: above ? r.top - 6 : r.bottom + 6, above });
  };
  return (
    <span ref={ref} className="inline-flex" onMouseEnter={show} onMouseLeave={() => setPos(null)}>
      <span className="w-3.5 h-3.5 rounded-full border border-gray-400 text-gray-400 text-[9px] flex items-center justify-center cursor-help select-none">i</span>
      {pos && createPortal(
        <span
          style={{ position: "fixed", left: pos.left, top: pos.top, transform: pos.above ? "translate(-50%, -100%)" : "translate(-50%, 0)", zIndex: 9999, pointerEvents: "none" }}
          className="px-2 py-1.5 text-xs bg-gray-800 text-white rounded shadow-lg w-60 text-left whitespace-normal"
        >
          {text}
        </span>,
        document.body
      )}
    </span>
  );
};

// ── Legend arrow swatch — mirrors the graph's edge line + ArrowClosed marker ──

const LegendArrow = ({ color, dashed }) => (
  <svg width="32" height="10" viewBox="0 0 32 10" aria-hidden="true" className="block">
    <line
      x1="1" y1="5" x2="23" y2="5"
      stroke={color} strokeWidth="2" strokeLinecap="round"
      strokeDasharray={dashed ? "4 3" : undefined}
    />
    <polygon points="22,0.5 31,5 22,9.5" fill={color} />
  </svg>
);

// ── Legend items per mode ─────────────────────────────────────────────────────

const DEVIATION_EDGE_LEGEND = { label: "Extra transition", color: "#fb923c", dashed: true }; // orange-400

const SEQUENTIAL_LEGEND = [
  { label: "Match",        color: "#4ade80" }, // green-400
  { label: "Missing Step", color: "#f87171" }, // red-400
  { label: "Extra Step",   color: "#facc15" }, // yellow-400
  DEVIATION_EDGE_LEGEND,
];

const TEMPORAL_LEGEND = [
  { label: "Within expected", color: "#4ade80" }, // green-400
  { label: "Slightly over",   color: "#facc15" }, // yellow-400
  { label: "Strongly over",   color: "#f87171" }, // red-400
  DEVIATION_EDGE_LEGEND,
];

// ── Mode descriptions (for tooltip) ──────────────────────────────────────────

const MODE_INFO = {
  sequential: "Shows whether the right process follows the same sequence of steps as the reference model. Red edges indicate transitions that exist in the observed process but not in the reference.",
  temporal:   "Shows whether transitions between activities happen within the expected time window. Edge colors indicate how strongly the actual duration deviates from the reference.",
};

const PROFILING_MODE_INFO = {
  standard: "Mean ± Std Dev: the expected corridor is built from the arithmetic mean and standard deviation of reference durations. Works well when transition times are roughly symmetrical, but sensitive to outliers. A few extreme durations can widen the corridor significantly.",
  robust:   "Median ± MAD: the expected corridor is built from the median and Median Absolute Deviation. Resistant to outliers, ideal when a small number of extreme transitions would otherwise distort the expected range.",
};

// ── Main component ────────────────────────────────────────────────────────────

const SplitGraphView = ({
  visualResult, sequentialResult, temporalResult, onReset,
  profilingMode, setProfilingMode, zeta, setZeta, onRerunTemporal, loadingTemporal,
}) => {
  const { reference_graph, observed_graph, conformance_mapping } = visualResult;

  const [mode, setMode] = useState("sequential"); // 'sequential' | 'temporal'
  const [localZeta, setLocalZeta] = useState(zeta);
  useEffect(() => { setLocalZeta(zeta); }, [zeta]);
  const [yellowThreshold, setYellowThreshold] = useState(20); // percentage
  const [redThreshold, setRedThreshold] = useState(50);       // percentage
  const [elkGraphHeight, setElkGraphHeight] = useState(0);
  const [sequentialAnnotation, setSequentialAnnotation] = useState("relative"); // 'relative' | 'absolute'

  const canvasHeight = elkGraphHeight > 0 ? elkGraphHeight + 150 : 400;

  const [selectedEdgeKey, setSelectedEdgeKey] = useState(null);
  const [clickPos, setClickPos] = useState(null);
  const graphWrapperRef = useRef(null);

  // Object-ID filter state (temporal mode only)
  const [objectFilterIds, setObjectFilterIds] = useState(null); // null = aggregate; Set<string> = filtered
  const [showObjectSelector, setShowObjectSelector] = useState(false);
  const [objectSearch, setObjectSearch] = useState("");
  const selectorRef = useRef(null);

  // Close detail panel (and filter selector) when switching modes; clear filter too
  useEffect(() => {
    setSelectedEdgeKey(null);
    setClickPos(null);
    setObjectFilterIds(null);
    setShowObjectSelector(false);
  }, [mode]);

  // Close the Object-ID selector when clicking outside it
  useEffect(() => {
    if (!showObjectSelector) return;
    const handler = (e) => {
      if (selectorRef.current && !selectorRef.current.contains(e.target)) {
        setShowObjectSelector(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showObjectSelector]);

  // All Object-IDs available in the temporal result (sorted)
  const allObjectIds = useMemo(() => {
    if (!temporalResult?.trace_results) return [];
    return [...new Set(temporalResult.trace_results.map((t) => t.object_id))].sort();
  }, [temporalResult]);

  // All Object-IDs available in the sequential result (sorted)
  const seqAllObjectIds = useMemo(() => {
    const seqObjects = visualResult?.observed_object_sequences;
    if (!seqObjects?.length) return [];
    return [...new Set(seqObjects.map((s) => s.object_id))].sort();
  }, [visualResult]);

  // Active pool switches by mode
  const activeAllObjectIds = mode === "temporal" ? allObjectIds : seqAllObjectIds;

  // Derive the filtered observed graph from selected object IDs using trace-level data.
  // Prefers temporal traces; falls back to sequential object sequences so filtering works
  // even when the temporal check failed.
  const filteredObservedGraph = useMemo(() => {
    if (!objectFilterIds || objectFilterIds.size === 0) return null;
    const temporalTraces = temporalResult?.trace_results;
    const seqObjects = visualResult?.observed_object_sequences;
    if (!temporalTraces && !seqObjects) return null;
    const totalObjects = temporalTraces
      ? new Set(temporalTraces.map((t) => t.object_id)).size
      : seqObjects.length;
    if (objectFilterIds.size >= totalObjects) return null;
    const activitySet = new Set();
    const edgeCounter = new Map();
    if (temporalTraces) {
      for (const trace of temporalTraces) {
        if (!objectFilterIds.has(trace.object_id)) continue;
        for (const pair of trace.activity_pairs) {
          activitySet.add(pair.source_activity);
          activitySet.add(pair.target_activity);
          const key = `${pair.source_activity}→${pair.target_activity}`;
          edgeCounter.set(key, (edgeCounter.get(key) ?? 0) + 1);
        }
      }
    } else {
      for (const obj of seqObjects) {
        if (!objectFilterIds.has(obj.object_id)) continue;
        const acts = obj.activities;
        for (const act of acts) activitySet.add(act);
        for (let i = 0; i < acts.length - 1; i++) {
          const key = `${acts[i]}→${acts[i + 1]}`;
          edgeCounter.set(key, (edgeCounter.get(key) ?? 0) + 1);
        }
      }
    }
    if (activitySet.size === 0) return null;
    return {
      nodes: [...activitySet].sort().map((id) => ({ id, label: id, conformance_status: "match" })),
      edges: [...edgeCounter.entries()].map(([key, frequency]) => {
        const arrow = key.indexOf("→");
        return { source: key.slice(0, arrow), target: key.slice(arrow + 1), frequency };
      }),
    };
  }, [objectFilterIds, temporalResult, visualResult]);

  // Swap observed_graph only when a filter is active; reference graph always unchanged
  const effectiveVisualResult = useMemo(
    () => filteredObservedGraph ? { ...visualResult, observed_graph: filteredObservedGraph } : visualResult,
    [visualResult, filteredObservedGraph],
  );

  const handleFilterByObjectIds = useCallback((ids) => {
    setObjectFilterIds(new Set(ids));
    setShowObjectSelector(false);
  }, []);

  const handleToggleObjectId = useCallback((id) => {
    setObjectFilterIds((prev) => {
      const next = new Set(prev ?? []);
      next.has(id) ? next.delete(id) : next.add(id);
      return next.size > 0 ? next : null;
    });
  }, []);

  const handleEdgeClick = useCallback((key, clientX, clientY) => {
    setSelectedEdgeKey(key);
    if (clientX !== undefined) {
      const panelWidth = 300;
      const offset = 16;
      const panelLeft = clientX + offset + panelWidth <= window.innerWidth
        ? clientX + offset
        : Math.max(0, clientX - offset - panelWidth);
      setClickPos({ left: panelLeft, top: Math.max(8, clientY - 40) });
    } else {
      setClickPos(null);
    }
  }, []);
  const handlePanelClose = useCallback(() => { setSelectedEdgeKey(null); setClickPos(null); }, []);

  // Pre-compute per-pair temporal aggregates from trace-level data
  const temporalEdgeMap = useMemo(() => {
    if (!temporalResult?.trace_results) return null;
    const map = {};
    for (const trace of temporalResult.trace_results) {
      for (const pair of trace.activity_pairs) {
        const key = `${pair.source_activity}→${pair.target_activity}`;
        if (!map[key]) map[key] = { expected_center: pair.expected_center, observed_durations: [], object_observations: [] };
        map[key].observed_durations.push(pair.observed_duration);
        map[key].object_observations.push({ object_id: trace.object_id, duration: pair.observed_duration });
      }
    }
    for (const d of Object.values(map)) {
      d.mean_observed = d.observed_durations.reduce((a, b) => a + b, 0) / d.observed_durations.length;
      d.pct_deviation = d.expected_center > 0
        ? (d.mean_observed - d.expected_center) / d.expected_center
        : 0;
    }
    return map;
  }, [temporalResult]);

  // Per-edge lifecycle-ending object IDs derived from sequential object sequences
  const sequentialEdgeLifecycleMap = useMemo(() => {
    const seqObjects = visualResult?.observed_object_sequences;
    if (!seqObjects?.length) return {};
    const map = {};
    for (const { object_id, activities } of seqObjects) {
      if (!activities.length) continue;
      const lastActivity = activities[activities.length - 1];
      const seen = new Set();
      for (let i = 0; i < activities.length - 1; i++) {
        seen.add(`${activities[i]}→${activities[i + 1]}`);
      }
      for (const key of seen) {
        const tgt = key.slice(key.indexOf("→") + 1);
        if (tgt === lastActivity) {
          if (!map[key]) map[key] = new Set();
          map[key].add(object_id);
        }
      }
    }
    return map;
  }, [visualResult]);

  // Per-edge lifecycle-ending object IDs derived from temporal trace results
  const temporalEdgeLifecycleMap = useMemo(() => {
    if (!temporalResult?.trace_results) return {};
    const map = {};
    for (const trace of temporalResult.trace_results) {
      const pairs = trace.activity_pairs;
      if (!pairs.length) continue;
      const lastActivity = pairs[pairs.length - 1].target_activity;
      const seen = new Set();
      for (const pair of pairs) {
        seen.add(`${pair.source_activity}→${pair.target_activity}`);
      }
      for (const key of seen) {
        const tgt = key.slice(key.indexOf("→") + 1);
        if (tgt === lastActivity) {
          if (!map[key]) map[key] = new Set();
          map[key].add(trace.object_id);
        }
      }
    }
    return map;
  }, [temporalResult]);

  // Per-edge set of ALL object IDs that traverse the edge in sequential mode
  const sequentialEdgeTraversalMap = useMemo(() => {
    const seqObjects = visualResult?.observed_object_sequences;
    if (!seqObjects?.length) return {};
    const map = {};
    for (const { object_id, activities } of seqObjects) {
      for (let i = 0; i < activities.length - 1; i++) {
        const key = `${activities[i]}→${activities[i + 1]}`;
        if (!map[key]) map[key] = new Set();
        map[key].add(object_id);
      }
    }
    return map;
  }, [visualResult]);

  // Active lifecycle map — switches with mode
  const lifecycleEndingMap = mode === "temporal" ? temporalEdgeLifecycleMap : sequentialEdgeLifecycleMap;

  // Pre-compute observed edge frequencies for sequential annotations
  const observedEdgeFreqMap = useMemo(() => {
    const edges = visualResult?.observed_graph?.edges;
    if (!edges?.length) return null;
    const outgoing = {};
    for (const e of edges) outgoing[e.source] = (outgoing[e.source] ?? 0) + (e.frequency ?? 0);
    const map = {};
    for (const e of edges) {
      const count = e.frequency ?? 0;
      const total = outgoing[e.source] ?? 1;
      map[`${e.source}→${e.target}`] = { count, relativeProb: total > 0 ? count / total : 0 };
    }
    return map;
  }, [visualResult]);

  const legend = mode === "temporal" ? TEMPORAL_LEGEND : SEQUENTIAL_LEGEND;

  return (
    <div className="flex flex-col gap-3">
      {/* Header row */}
      <div className="flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-4">
          {/* Mode toggle */}
          <div className="flex items-center gap-1.5">
            <div className="flex rounded-md border border-gray-300 overflow-hidden text-xs divide-x divide-gray-300">
              {["sequential", "temporal"].map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMode(m)}
                  className={`px-3 py-1 capitalize transition-colors ${mode === m ? "bg-blue-600 text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
                >
                  {m}
                </button>
              ))}
            </div>
            <Tooltip text={MODE_INFO[mode]} />
          </div>
        </div>

        <button
          onClick={onReset}
          className="text-sm text-gray-500 hover:text-gray-700 underline underline-offset-2"
        >
          ← New Check
        </button>
      </div>

      {/* Stats row */}
      <div className="flex gap-4 flex-shrink-0 text-xs text-gray-500">
        <span>
          Reference: <strong className="text-gray-700">{reference_graph.nodes.length}</strong> activities
        </span>
        <span>
          Observed: <strong className="text-gray-700">{observed_graph.nodes.length}</strong> activities
        </span>
        <span>
          Matching:{" "}
          <strong className="text-green-700">
            {Object.values(conformance_mapping).filter((s) => s === "match").length}
          </strong>
        </span>
        <span>
          Missing:{" "}
          <strong className="text-red-700">
            {Object.values(conformance_mapping).filter((s) => s === "missing_step").length}
          </strong>
        </span>
        <span>
          Extra:{" "}
          <strong className="text-yellow-700">
            {Object.values(conformance_mapping).filter((s) => s === "extra_step").length}
          </strong>
        </span>
      </div>

      {/* Profiling method + zeta corridor — visible only in Temporal Mode */}
      {mode === "temporal" && (
        <div className="flex items-center gap-4 flex-shrink-0 text-xs text-gray-500">
          <span className="font-medium">Method:</span>
          <div className="flex rounded-md border border-gray-300 overflow-hidden text-xs divide-x divide-gray-300">
            {[
              { value: "standard", label: "Mean ± Std Dev" },
              { value: "robust",   label: "Median ± MAD" },
            ].map(({ value, label }) => (
              <button
                key={value}
                type="button"
                disabled={loadingTemporal}
                onClick={() => {
                  setProfilingMode(value);
                  onRerunTemporal(localZeta, value);
                }}
                className={`px-3 py-1 transition-colors ${profilingMode === value ? "bg-blue-600 text-white" : "bg-white text-gray-600 hover:bg-gray-50"} disabled:opacity-50`}
              >
                {label}
              </button>
            ))}
          </div>
          <Tooltip text={PROFILING_MODE_INFO[profilingMode]} />

          <span className="font-medium ml-2">ζ corridor:</span>
          <div className="flex items-center gap-2">
            <div className="relative flex items-center w-32" style={{ height: 20 }}>
              <div className="absolute w-full h-1.5 rounded-full bg-gray-200" />
              <div
                className="absolute h-1.5 rounded-full bg-blue-600 pointer-events-none"
                style={{ width: `${((localZeta - 0.5) / 4.5) * 100}%` }}
              />
              <input
                type="range" min="0.5" max="5" step="0.5"
                value={localZeta}
                disabled={loadingTemporal}
                onChange={(e) => setLocalZeta(parseFloat(e.target.value))}
                onMouseUp={(e) => {
                  const val = parseFloat(e.target.value);
                  setZeta(val);
                  onRerunTemporal(val, profilingMode);
                }}
                onTouchEnd={(e) => {
                  const val = parseFloat(e.target.value);
                  setZeta(val);
                  onRerunTemporal(val, profilingMode);
                }}
                className="absolute w-full cursor-pointer disabled:opacity-50"
                style={{ opacity: 0, height: 20, margin: 0 }}
              />
              <div
                className="absolute w-4 h-4 rounded-full bg-blue-600 border-2 border-white shadow pointer-events-none"
                style={{ left: `calc(${((localZeta - 0.5) / 4.5) * 100}% - 8px)` }}
              />
            </div>
            <span className="font-mono text-gray-500 w-6">{localZeta.toFixed(1)}</span>
            {loadingTemporal && <span className="text-gray-400 italic">updating…</span>}
          </div>
        </div>
      )}

      {/* Filter-active indicator — visible only in Sequential Mode when a filter is set */}
      {mode === "sequential" && objectFilterIds && (
        <div className="flex items-center gap-3 flex-shrink-0 text-xs text-gray-500">
          <span className="text-blue-700 font-medium">
            Filtered: {objectFilterIds.size} object{objectFilterIds.size !== 1 ? "s" : ""}
          </span>
          <button
            type="button"
            onClick={() => setObjectFilterIds(null)}
            className="text-gray-400 hover:text-gray-600 text-xs underline"
          >
            Clear
          </button>
        </div>
      )}

      {/* Sequential annotation toggle — visible only in Sequential Mode */}
      {mode === "sequential" && (
        <div className="flex items-center gap-3 flex-shrink-0 text-xs text-gray-500">
          <span className="font-medium">Edge labels:</span>
          <div className="flex rounded-md border border-gray-300 overflow-hidden text-xs divide-x divide-gray-300">
            {[{ value: "relative", label: "Probability %" }, { value: "absolute", label: "Count" }].map(({ value, label }) => (
              <button
                key={value}
                type="button"
                onClick={() => setSequentialAnnotation(value)}
                className={`px-3 py-1 transition-colors ${sequentialAnnotation === value ? "bg-blue-600 text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Sequential conformance summary — visible only in Sequential Mode */}
      {mode === "sequential" && sequentialResult && (
        <div className="flex-shrink-0">
          <ConformanceSummary results={sequentialResult} />
        </div>
      )}

      {/* Temporal results table — visible only in Temporal Mode */}
      {mode === "temporal" && temporalResult && (
        <div className="flex-shrink-0">
          <TemporalResults results={temporalResult} onPairClick={handleEdgeClick} />
        </div>
      )}

      {/* Temporal threshold controls — only in Temporal Mode */}
      {mode === "temporal" && (
        <div className="flex items-center gap-3 flex-shrink-0 text-xs text-gray-500">
          <span className="font-medium">Thresholds:</span>
          <label className="flex items-center gap-1">
            Yellow &gt;
            <input
              type="number"
              min={1}
              max={redThreshold - 1}
              value={yellowThreshold}
              onChange={(e) => setYellowThreshold(Math.max(1, Math.min(Number(e.target.value), redThreshold - 1)))}
              className="w-14 border border-gray-300 rounded px-1.5 py-0.5 text-center text-gray-700"
            />
            %
          </label>
          <label className="flex items-center gap-1">
            Red &gt;
            <input
              type="number"
              min={yellowThreshold + 1}
              max={999}
              value={redThreshold}
              onChange={(e) => setRedThreshold(Math.max(yellowThreshold + 1, Number(e.target.value)))}
              className="w-14 border border-gray-300 rounded px-1.5 py-0.5 text-center text-gray-700"
            />
            %
          </label>
        </div>
      )}

      {/* Object-ID filter toggle — visible in both modes when object data is available */}
      {activeAllObjectIds.length > 0 && (
        <div className="flex items-center gap-3 flex-shrink-0 text-xs text-gray-500">
          <span className="font-medium">Observed:</span>

          {/* All Objects pill */}
          <button
            type="button"
            onClick={() => { setObjectFilterIds(null); setShowObjectSelector(false); }}
            className={`px-3 py-1 rounded-md border text-xs transition-colors ${
              !objectFilterIds
                ? "bg-blue-600 text-white border-blue-600"
                : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
            }`}
          >
            All Objects
          </button>

          {/* Filter by Object-ID pill + dropdown */}
          <div className="relative" ref={selectorRef}>
            <button
              type="button"
              onClick={() => setShowObjectSelector((v) => !v)}
              className={`px-3 py-1 rounded-md border text-xs transition-colors flex items-center gap-1.5 ${
                objectFilterIds
                  ? "bg-blue-50 text-blue-700 border-blue-300"
                  : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
              }`}
            >
              Filter by Object-ID
              {objectFilterIds && (
                <span className="bg-blue-600 text-white rounded-full px-1.5 text-[9px] font-bold leading-tight">
                  {objectFilterIds.size}
                </span>
              )}
            </button>

            {showObjectSelector && (
              <div className="absolute top-full left-0 mt-1 w-72 bg-white border border-gray-200 rounded-xl shadow-xl z-20 flex flex-col max-h-64">
                <div className="p-2 border-b border-gray-100 flex-shrink-0">
                  <input
                    autoFocus
                    type="text"
                    placeholder="Search Object-IDs…"
                    value={objectSearch}
                    onChange={(e) => setObjectSearch(e.target.value)}
                    className="w-full text-xs border border-gray-200 rounded-md px-2 py-1 outline-none focus:border-blue-400"
                  />
                </div>
                <ul className="overflow-y-auto flex-1 py-1">
                  {activeAllObjectIds
                    .filter((id) => id.toLowerCase().includes(objectSearch.toLowerCase()))
                    .map((id) => (
                      <li
                        key={id}
                        onClick={() => handleToggleObjectId(id)}
                        className={`px-3 py-1 text-xs font-mono cursor-pointer hover:bg-gray-50 flex items-center gap-2 ${
                          objectFilterIds?.has(id) ? "text-blue-700 font-semibold" : "text-gray-600"
                        }`}
                      >
                        <span
                          className={`w-3 h-3 rounded border flex-shrink-0 flex items-center justify-center text-[8px] ${
                            objectFilterIds?.has(id)
                              ? "bg-blue-600 border-blue-600 text-white"
                              : "border-gray-300"
                          }`}
                        >
                          {objectFilterIds?.has(id) ? "✓" : ""}
                        </span>
                        <span className="truncate">{id}</span>
                      </li>
                    ))}
                </ul>
              </div>
            )}
          </div>

          {/* Clear link — only when a filter is active */}
          {objectFilterIds && (
            <button
              type="button"
              onClick={() => setObjectFilterIds(null)}
              className="text-gray-400 hover:text-gray-600 text-xs underline"
            >
              Clear
            </button>
          )}
        </div>
      )}

      {/* Legend */}
      <div className="flex items-center gap-5 flex-shrink-0">
        {legend.map(({ label, color, dashed }) => (
          <div key={label} className="flex flex-col items-center gap-0.5">
            <span className="text-xs font-medium text-gray-700">{label}</span>
            <LegendArrow color={color} dashed={dashed} />
          </div>
        ))}
      </div>

      {/* Single synchronized graph canvas — both subgraphs in one ReactFlow instance */}
      <div ref={graphWrapperRef}>
        <div
          className="border border-gray-200 rounded-xl overflow-hidden bg-white"
          style={{ position: "relative", height: canvasHeight }}
        >
          <SplitConformanceGraph
            visualResult={effectiveVisualResult}
            mode={mode}
            temporalEdgeMap={temporalEdgeMap}
            yellowThreshold={yellowThreshold / 100}
            redThreshold={redThreshold / 100}
            onHeightChange={setElkGraphHeight}
            onEdgeClick={handleEdgeClick}
            onPaneClick={handlePanelClose}
            observedEdgeFreqMap={observedEdgeFreqMap}
            sequentialAnnotation={sequentialAnnotation}
          />
        </div>
      </div>
      {selectedEdgeKey && (mode === "sequential" || !!temporalEdgeMap?.[selectedEdgeKey]) && createPortal(
        <EdgeDetailPanel
          key={selectedEdgeKey}
          edgeKey={selectedEdgeKey}
          pairData={mode === "temporal" ? temporalEdgeMap?.[selectedEdgeKey] : null}
          onClose={handlePanelClose}
          panelStyle={clickPos ?? { top: 8, right: 8 }}
          onFilterByObjectIds={handleFilterByObjectIds}
          activeObjectFilter={objectFilterIds}
          lifecycleEndingIds={lifecycleEndingMap?.[selectedEdgeKey] ?? new Set()}
          traversalObjectIds={
            mode === "sequential"
              ? (sequentialEdgeTraversalMap?.[selectedEdgeKey] ?? new Set())
              : null
          }
        />,
        document.body,
      )}
    </div>
  );
};

export default SplitGraphView;
